/**
 * useNoteAnalytics.js
 *
 * A self-contained custom React hook that adds accurate GA4 engagement
 * tracking to the NotePreview component.
 *
 * Design principles:
 *  - Zero impact on existing UI, CSS, or component rendering.
 *  - All state lives in refs so no extra re-renders are triggered.
 *  - Heartbeat pauses automatically when tab/window loses focus.
 *  - Every timer and listener is cleaned up on unmount.
 *  - Milestone events fire at most once per component mount.
 *  - Scroll depth events fire at most once per 10 % increment.
 *  - note_completed fires at most once.
 *  - No actual search keywords are sent (privacy-safe).
 *
 * Usage in NotePreview.js:
 *   const { trackSearch, trackZoom, trackShare, trackPin } =
 *     useNoteAnalytics({ course, semester, subject, fileName, fileExists, iframeRef });
 */

import { useEffect, useRef, useCallback } from "react";

// ─── Milestone thresholds (seconds) ───────────────────────────────────────────
const MILESTONES = [30, 60, 120, 300, 600, 900, 1800, 3600];

// ─── Heartbeat interval ────────────────────────────────────────────────────────
const HEARTBEAT_INTERVAL_MS = 30_000; // 30 seconds

// ─── Helper: fire a GA4 custom event ──────────────────────────────────────────
function gtagEvent(name, params) {
  if (typeof window.gtag === "function") {
    window.gtag("event", name, params);
  }
}

// ─── Hook ──────────────────────────────────────────────────────────────────────
export function useNoteAnalytics({
  course,
  semester,
  subject,
  fileName,
  fileExists,
  iframeRef,
}) {
  // ── Shared note metadata sent with every event ─────────────────────────────
  const noteMeta = { course, semester, subject, file_name: fileName };

  // ── Refs (no re-renders) ───────────────────────────────────────────────────
  const openTimeRef        = useRef(null);   // Date.now() when note_open was fired
  const activeSecsRef      = useRef(0);      // Total active (tab-visible) seconds
  const isActiveRef        = useRef(true);   // Whether the tab/window is currently visible+focused
  const heartbeatRef       = useRef(null);   // setInterval handle
  const milestonesHitRef   = useRef(new Set()); // Milestones already fired this session
  const scrollDepthRef     = useRef(0);      // Highest 10 % bucket reported so far
  const completedFiredRef  = useRef(false);  // Whether note_completed was fired
  const scrollCleanupRef   = useRef(null);   // Fn to remove iframe scroll listener

  // ── Stable references for the note metadata object ────────────────────────
  // We rebuild noteMeta inline so this hook works without needing useMemo.

  // ── 1. note_open ───────────────────────────────────────────────────────────
  useEffect(() => {
    // Only fire once fileExists is confirmed (avoids events for missing notes)
    if (!fileExists) return;

    openTimeRef.current = Date.now();
    gtagEvent("note_open", { ...noteMeta });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileExists, fileName]); // re-fire if the user opens a different note

  // ── 2. note_close (fires on unmount) ──────────────────────────────────────
  useEffect(() => {
    if (!fileExists) return;

    return () => {
      const reading_seconds = Math.round(activeSecsRef.current);
      gtagEvent("note_close", { ...noteMeta, reading_seconds });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileExists, fileName]);

  // ── 3. Heartbeat + visibility/focus control ────────────────────────────────
  useEffect(() => {
    if (!fileExists) return;

    // Tick function — called every HEARTBEAT_INTERVAL_MS of active time
    const tick = () => {
      if (!isActiveRef.current) return;

      // Accumulate active seconds
      activeSecsRef.current += HEARTBEAT_INTERVAL_MS / 1000;
      const elapsed_seconds = Math.round(activeSecsRef.current);

      // Fire heartbeat
      gtagEvent("reading_progress", { ...noteMeta, elapsed_seconds });

      // Check milestones
      MILESTONES.forEach((threshold) => {
        if (
          elapsed_seconds >= threshold &&
          !milestonesHitRef.current.has(threshold)
        ) {
          milestonesHitRef.current.add(threshold);
          gtagEvent(`reading_${threshold}_seconds`, { ...noteMeta, elapsed_seconds });
        }
      });
    };

    // Start heartbeat
    heartbeatRef.current = setInterval(tick, HEARTBEAT_INTERVAL_MS);

    // Pause/resume on tab visibility change
    const handleVisibilityChange = () => {
      isActiveRef.current = document.visibilityState === "visible";
    };

    // Pause/resume on window focus/blur
    const handleFocus = () => { isActiveRef.current = true; };
    const handleBlur  = () => { isActiveRef.current = false; };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur",  handleBlur);

    return () => {
      clearInterval(heartbeatRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur",  handleBlur);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileExists, fileName]);

  // ── 4. Scroll depth tracking inside iframe ─────────────────────────────────
  useEffect(() => {
    if (!fileExists) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    // Throttle flag — prevents spamming on rapid scroll
    let throttleTimer = null;

    const handleScroll = () => {
      if (throttleTimer) return; // already scheduled
      throttleTimer = setTimeout(() => {
        throttleTimer = null;

        try {
          const doc  = iframe.contentDocument || iframe.contentWindow.document;
          const root = doc.scrollingElement || doc.documentElement;
          const maxScroll = root.scrollHeight - root.clientHeight;
          if (maxScroll <= 0) return;

          const rawPercent = root.scrollTop / maxScroll;           // 0 – 1
          const bucket     = Math.floor(rawPercent * 10) * 10;     // 0, 10, 20 … 100

          // Fire note_scroll for every new 10 % bucket crossed
          if (bucket > scrollDepthRef.current) {
            scrollDepthRef.current = bucket;
            gtagEvent("note_scroll", {
              ...noteMeta,
              scroll_depth_percent: bucket,
            });
          }

          // note_completed at >= 90 %
          if (rawPercent >= 0.9 && !completedFiredRef.current) {
            completedFiredRef.current = true;
            gtagEvent("note_completed", { ...noteMeta });
          }
        } catch (_) {
          // Cross-origin or iframe not ready — silently skip
        }
      }, 250); // 250 ms throttle — lightweight
    };

    // Attach listener after the iframe has fully loaded
    const attachScrollListener = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.addEventListener("scroll", handleScroll, { passive: true });

        // Store cleanup so we can remove it on unmount
        scrollCleanupRef.current = () => {
          doc.removeEventListener("scroll", handleScroll);
          if (throttleTimer) clearTimeout(throttleTimer);
        };
      } catch (_) {}
    };

    iframe.addEventListener("load", attachScrollListener);

    return () => {
      iframe.removeEventListener("load", attachScrollListener);
      if (scrollCleanupRef.current) {
        scrollCleanupRef.current();
        scrollCleanupRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileExists, fileName]);

  // ── 5. Exposed one-liner helpers called from existing handlers ─────────────

  /** Call after a search completes. Do NOT pass the actual keyword. */
  const trackSearch = useCallback(
    (termLength, matchCount) => {
      gtagEvent("note_search", {
        ...noteMeta,
        term_length:  termLength,
        match_count:  matchCount,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [course, semester, subject, fileName]
  );

  /** Call with the new zoom scale (number, e.g. 1.5) after zoom changes. */
  const trackZoom = useCallback(
    (zoomLevel) => {
      gtagEvent("note_zoom", {
        ...noteMeta,
        zoom_level: zoomLevel,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [course, semester, subject, fileName]
  );

  /** Call when the share action is triggered. */
  const trackShare = useCallback(
    () => {
      gtagEvent("note_share", { ...noteMeta });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [course, semester, subject, fileName]
  );

  /**
   * Call when pin state changes.
   * @param {boolean} pinned — true when the note was just pinned.
   */
  const trackPin = useCallback(
    (pinned) => {
      gtagEvent(pinned ? "note_pin" : "note_unpin", { ...noteMeta });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [course, semester, subject, fileName]
  );

  return { trackSearch, trackZoom, trackShare, trackPin };
}
